import { FormEvent, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Gavel } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const { user, passwordRecovery } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [nickname, setNickname] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (location.state?.passwordUpdated) {
      setMessage('Password aggiornata. Ora puoi accedere.');
    }
  }, [location.state]);

  if (passwordRecovery) return <Navigate to="/reset-password" replace />;
  if (user) return <Navigate to="/" replace />;

  const resolveEmail = async (value: string) => {
    const clean = value.trim();
    if (clean.includes('@')) return clean;
    const { data, error } = await supabase.rpc('auth_email_for_nickname', { p_nickname: clean });
    if (error) throw error;
    if (!data) throw new Error('Nickname non trovato.');
    return data as string;
  };

  const login = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const loginEmail = await resolveEmail(identifier);
      const { error } = await supabase.auth.signInWithPassword({ email: loginEmail, password });
      if (error) throw error;
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Accesso non riuscito.');
    } finally {
      setBusy(false);
    }
  };

  const signup = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const { error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            nickname: nickname.trim(),
            display_name: displayName.trim() || nickname.trim()
          }
        }
      });
      if (error) throw error;
      setMessage('Account creato. Controlla l’email se la conferma è attiva.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Registrazione non riuscita.');
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (!identifier.trim()) {
      setMessage('Inserisci prima la tua email o il tuo nickname.');
      return;
    }

    setBusy(true);
    setMessage(null);
    try {
      const target = await resolveEmail(identifier);
      const { error } = await supabase.auth.resetPasswordForEmail(target, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) throw error;
      setMessage('Email di recupero inviata. Controlla anche la cartella spam.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Invio dell’email non riuscito.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-screen">
      <section className="login-card glass">
        <div className="login-logo"><Gavel size={34} /></div>
        <h1>Aste Euroleghe</h1>
        <p className="muted">PWA privata per rilanci live, chat e notifiche.</p>

        <div className="segmented">
          <button className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')}>Accedi</button>
          <button className={mode === 'signup' ? 'active' : ''} onClick={() => setMode('signup')}>Crea account</button>
        </div>

        {mode === 'login' ? (
          <form onSubmit={login} className="form-stack">
            <label>Email o nickname<input value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" required /></label>
            <label>Password<input value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="current-password" required /></label>
            <button className="primary-button full" disabled={busy}>{busy ? 'Accesso…' : 'Entra'}</button>
            <button type="button" className="link-button" onClick={resetPassword}>Password dimenticata?</button>
          </form>
        ) : (
          <form onSubmit={signup} className="form-stack">
            <label>Email<input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" required /></label>
            <label>Nickname<input value={nickname} onChange={(e) => setNickname(e.target.value)} autoComplete="nickname" required /></label>
            <label>Nome visualizzato<input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" /></label>
            <label>Password<input value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="new-password" minLength={8} required /></label>
            <button className="primary-button full" disabled={busy}>{busy ? 'Creo…' : 'Crea account'}</button>
          </form>
        )}

        {message && <p className="message-box">{message}</p>}
        <p className="ios-note">Su iPhone: apri da Safari → Condividi → Aggiungi alla schermata Home.</p>
      </section>
    </main>
  );
}
